import { type ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft } from 'lucide-react'

type CommitteeEvent = {
  id: string
  name: string | null
  date: string | null
  city: string | null
  state: string | null
  status: string | null
  archived: boolean
  organization_id: string
  organization_name: string
}

type MemberProfile = {
  id: string
  name: string
  title: string | null
  industry: string | null
  committee_type: string
  committee_label: string
  event: CommitteeEvent
}

type HistoryEntry = {
  id: string
  name: string
  title: string | null
  industry: string | null
  committee_type: string
  committee_label: string
  event: CommitteeEvent
}

type Props = {
  member: MemberProfile
  history: HistoryEntry[]
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchCommitteeMemberShow({ member, history }: Props) {
  const backPath = `/admin/research/${member.committee_type}_committee_members`

  return (
    <>
      <Head title={`Research - ${member.name}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="mb-4">
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link href={backPath}>
              <ArrowLeft className="mr-2 size-4" />
              {member.committee_label}
            </Link>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">{member.name}</h2>
          <Badge variant="secondary">{member.committee_label}</Badge>
          {member.title && <Badge variant="outline">{member.title}</Badge>}
          {member.industry && <Badge variant="outline">{member.industry}</Badge>}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <Card className="self-start">
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Current event</p>
                <Link
                  href={`/admin/research/events/${member.event.id}`}
                  className="font-medium hover:underline"
                >
                  {member.event.name || 'Untitled'}
                </Link>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Organization</p>
                <Link
                  href={`/admin/research/organizations/${member.event.organization_id}`}
                  className="font-medium hover:underline"
                >
                  {member.event.organization_name}
                </Link>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Date</p>
                <p>{formatDate(member.event.date)}</p>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2">
            <h3 className="mb-3 text-lg font-semibold tracking-tight">
              Committee history ({history.length})
            </h3>
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Committee</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Badge variant="secondary">{entry.committee_label}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        <Link
                          href={`/admin/research/events/${entry.event.id}`}
                          className="hover:underline"
                        >
                          {entry.event.name || 'Untitled'}
                        </Link>
                        {(entry.title || entry.industry) && (
                          <p className="text-xs font-normal text-muted-foreground">
                            {[entry.title, entry.industry].filter(Boolean).join(' / ')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Link
                          href={`/admin/research/organizations/${entry.event.organization_id}`}
                          className="hover:underline"
                        >
                          {entry.event.organization_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(entry.event.date)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {[entry.event.city, entry.event.state].filter(Boolean).join(', ') || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {entry.event.status && <Badge variant="outline">{entry.event.status}</Badge>}
                          {entry.event.archived && <Badge variant="secondary">Archived</Badge>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        No committee history found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

ResearchCommitteeMemberShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
